import { useQuery } from "@tanstack/react-query";
import { useRef, useCallback } from "react";
import { apiClient } from "../../../../zodios/client";
import { sleep } from "../../../../hooks/api";
import { getInverseExponentialBackoff } from "../../../../utils/backoff";

export const useAdvertiserAttributesPoll = (options?: { enabled?: boolean }) => {
  // Use ref to track transient force refresh request
  const forceRefreshRef = useRef(false);

  const pollQuery = useQuery({
    queryKey: ["advertiserAttributesPoll"],
    enabled: options?.enabled ?? true,
    queryFn: async ({ signal }) => {
      // Read and immediately reset the force refresh flag
      const shouldForceRefresh = forceRefreshRef.current;
      forceRefreshRef.current = false;

      let pollCount = 0;

      while (true) {
        // Check if the query was aborted before making the next request
        if (signal.aborted) {
          throw new Error("Polling cancelled");
        }

        const res = await apiClient.pollAdvertiserAttributes(
          {
            force_refresh: shouldForceRefresh,
          },
          { signal },
        );

        switch (res.status) {
          case "done":
            // Success - stop polling and return data
            return res;

          case "failed":
            // Handle failure with cooldown check
            if (res.can_retry_after) {
              const cooldownEnd = new Date(res.can_retry_after);
              const now = new Date();
              if (cooldownEnd > now) {
                throw new Error(`Snapshot failed. Can retry after ${cooldownEnd.toLocaleString()}`);
              }
            }
            throw new Error(res.error_message || "Snapshot retrieval failed");

          case "pending":
          case "processing":
          default:
            // Inverse exponential backoff: 30s, 15s, 7.5s, 3.75s, ~2s, 2s, ...
            await sleep(getInverseExponentialBackoff(pollCount));
            pollCount++;
        }
      }
    },
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Callback to trigger force refresh
  const forceRefresh = useCallback(() => {
    forceRefreshRef.current = true;
    void pollQuery.refetch();
  }, [pollQuery]);

  return [pollQuery, forceRefresh] as const;
};
